// Band-Masonry für das Projekt-Mosaik, nachgebaut nach der Live-Seite. Das alte
// Raster bestand aus ZWEI .large-6-Spalten nebeneinander, jede ein 6-Spalten-
// Mosaik. Die Kacheln folgen einem festen Größen-Rhythmus (groß, 2× klein,
// 2× mittel) und werden in "Bändern" zu je 6 Basis-Spalten gepackt:
//   • groß(4) + zwei kleine ÜBEREINANDER(2)  = 6 Spalten
//   • zwei mittlere NEBENEINANDER(3+3)        = 6 Spalten
// Auf breiten Screens laufen zwei solcher Gruppen nebeneinander; die Bänder
// wechseln zeilenweise die Seite, sodass sich
//   Zeile 1:  groß + 2 klein  |  mittel + mittel
//   Zeile 2:  mittel + mittel |  2 klein + groß
// abwechseln — wie auf der Live-Seite.
// Geometrie 1:1 live gemessen (375/600/800/1024/1440px):
//   • Gutter zwischen Kacheln = 1em der Grid-Schrift (17/18/19px)
//   • ab 768px (Foundation large) IMMER zwei Gruppen (.large-6-Zellen) mit
//     20px Abstand (2× 10px Zellpadding) und 10px Außenrand
//   • darunter ein schlichtes 2-Spalten-Raster gleich großer Quadrate,
//     Außenrand 10px ab 480px, darunter 0
const COLS = 6 // Basis-Spalten je Gruppe (klein=2, mittel=3, groß=4)
const SPAN = { small: 2, medium: 3, large: 4 }
const GROUP_GAP = 20
// Fester, positionsbasierter Größen-Rhythmus (wie das Original) — ergibt die
// Bänder [groß + 2 klein] und [2 mittel]. Positionsbasiert statt aus der
// migrierten tileSize, damit der Rhythmus auch nach dem Filtern sauber bleibt.
const PATTERN = ['large', 'small', 'small', 'medium', 'medium']

// Positioniert die (sichtbaren) Kacheln eines Grids absolut.
export function layoutMosaic(grid) {
	if (!grid) return
	const fullWidth = grid.clientWidth
	if (!fullWidth) return
	const GAP = parseFloat(getComputedStyle(grid).fontSize) || 19
	const vw = window.innerWidth
	const outer = vw >= 480 ? 10 : 0
	const width = fullWidth - 2 * outer

	const tiles = Array.from(grid.querySelectorAll('[data-project-item]')).filter(
		(t) => t.style.display !== 'none'
	)
	tiles.forEach((t, i) => {
		t.dataset.mosaicSize = PATTERN[i % PATTERN.length]
	})

	// Unter 768px: 2 Spalten gleich großer Quadrate in DOM-Reihenfolge.
	if (vw < 768) {
		const w = (width - GAP) / 2
		tiles.forEach((t, i) => {
			t.style.position = 'absolute'
			t.style.left = `${outer + (i % 2) * (w + GAP)}px`
			t.style.top = `${Math.floor(i / 2) * (w + GAP)}px`
			t.style.width = `${w}px`
			t.style.height = `${w}px`
		})
		const rows = Math.ceil(tiles.length / 2)
		grid.style.position = 'relative'
		grid.style.height = `${Math.max(0, rows * (w + GAP) - GAP)}px`
		return
	}

	const groups = 2
	const groupW = (width - GROUP_GAP * (groups - 1)) / groups
	const colW = (groupW - GAP * (COLS - 1)) / COLS

	// In Bänder (je ≤6 Spalten) gruppieren; zwei aufeinanderfolgende kleine
	// bilden eine gestapelte 2-Spalten-Säule.
	const bands = []
	let i = 0
	while (i < tiles.length) {
		const band = []
		let cols = 0
		while (i < tiles.length && cols < COLS) {
			const size = tiles[i].dataset.mosaicSize
			if (size === 'large') {
				band.push(tiles[i++])
				cols += 4
			} else if (size === 'medium') {
				band.push(tiles[i++])
				cols += 3
			} else {
				band.push(tiles[i++])
				if (i < tiles.length && tiles[i].dataset.mosaicSize === 'small') band.push(tiles[i++])
				cols += 2
			}
		}
		bands.push(band)
	}

	// Bänder auf die Gruppen-Spalten verteilen; in jeder zweiten "Zeile"
	// (Band-Paar) die Seite wechseln → großes Band links/rechts alternierend.
	const colY = new Array(groups).fill(0)
	let maxH = 0
	bands.forEach((band, bi) => {
		let g
		if (groups === 1) {
			g = 0
		} else {
			const pair = Math.floor(bi / groups)
			const within = bi % groups
			g = pair % 2 === 0 ? within : groups - 1 - within
		}
		const xOff = outer + g * (groupW + GROUP_GAP)
		const h = layoutBand(band, xOff, colY[g], colW, GAP)
		colY[g] += h + GAP
		maxH = Math.max(maxH, colY[g])
	})

	grid.style.position = 'relative'
	grid.style.height = `${Math.max(0, maxH - GAP)}px`
}

// Legt ein Band (≤6 Spalten) ab yStart ab; gibt die Bandhöhe zurück.
function layoutBand(band, xOff, yStart, colW, GAP) {
	const colH = new Array(COLS).fill(0)
	let prevSmall = null
	band.forEach((tile) => {
		const size = tile.dataset.mosaicSize || 'small'
		const span = Math.min(SPAN[size] || 2, COLS)
		const w = colW * span + GAP * (span - 1)
		let col
		if (size === 'small' && prevSmall && prevSmall.col + span <= COLS) {
			col = prevSmall.col
		} else {
			let best = { y: Infinity, col: 0 }
			for (let c = 0; c <= COLS - span; c++) {
				let y = 0
				for (let k = c; k < c + span; k++) y = Math.max(y, colH[k])
				if (y < best.y) best = { y, col: c }
			}
			col = best.col
		}
		let y = 0
		for (let k = col; k < col + span; k++) y = Math.max(y, colH[k])
		tile.style.position = 'absolute'
		tile.style.left = `${xOff + col * (colW + GAP)}px`
		tile.style.top = `${yStart + y}px`
		tile.style.width = `${w}px`
		tile.style.height = `${w}px`
		const bottom = y + w + GAP
		for (let k = col; k < col + span; k++) colH[k] = bottom
		prevSmall = size === 'small' && !prevSmall ? { col } : null
	})
	return Math.max(0, ...colH) - GAP
}

// Idempotenter Enhancer: alle Mosaik-Grids layouten + einmalig auf Resize neu
// packen. Läuft unabhängig vom Filter (auch auf der Startseite ohne Filterleiste).
export function initProjectMosaics() {
	const grids = Array.from(document.querySelectorAll('[data-project-grid-items]'))
	grids.forEach(layoutMosaic)
	if (grids.length && !window.__projectMosaicResize) {
		window.__projectMosaicResize = true
		let timer
		window.addEventListener('resize', () => {
			clearTimeout(timer)
			timer = setTimeout(() => {
				document.querySelectorAll('[data-project-grid-items]').forEach(layoutMosaic)
			}, 150)
		})
	}
}
